import { Dayjs } from "utils/dayjs"

import { Guid } from "@/types"
import { toDateTimeWithOffset } from "utils/utils"
import ReservationArticle, { IGetReservationArticleDto } from "./ReservationArticle"
import ReservationCartAsset, { IGetReservationCartAssetDto, IReservationCartAssetWithInvalidPeriod } from "./ReservationCartAsset"

export interface IGetReservationCartArticleDto extends IGetReservationArticleDto {
    assets: IGetReservationCartAssetDto[]
}

export interface IUpdateReservationCartArticleQuantity {
    articleId: Guid
    quantity: number
    start?: Dayjs | null
    end?: Dayjs | null
}

export interface IUpdateReservationCartArticleQuantityDto {
    articleId: Guid
    quantity: number
    start?: string | null
    end?: string | null
}

interface IGetAssetsWithInvalidPeriodParams {
    reservationStart: Dayjs
    reservationEnd: Dayjs | null
    lastNotCreditedInvoiceEndDate: Dayjs | null
}

export interface IUpdateReservationCartNodeArticleDto {
    articleId: Guid
    node: true
}

export interface IUpdateReservationCartArticleColorDto {
    articleId: Guid
    colorId?: Guid | null
}

export default class ReservationCartArticle extends ReservationArticle {
    assets: ReservationCartAsset[]

    isFixedAssetSelectionRequired: boolean

    constructor({
        dto,
        isFixedAssetSelectionRequired = false,
    }: {
        dto: IGetReservationCartArticleDto
        isFixedAssetSelectionRequired?: boolean
    }) {
        super(dto)
        this.assets = dto.assets.map(reservationCartAssetDto => new ReservationCartAsset(reservationCartAssetDto))
        this.isFixedAssetSelectionRequired = isFixedAssetSelectionRequired
    }

    get hasAutomaticSelectionAssets(): boolean {
        return this.assets.some(asset => asset.isAutomaticSelection)
    }

    get hasAssetsNeedingFixedSelection(): boolean {
        return this.assets.some(asset => asset.needsFixedAssetSelection)
    }

    get hasNotAvailableAssetWithUserAssignment(): boolean {
        return this.assets.some(asset => {
            return asset.assetUnavailable
                && asset.hasUserAssignment
        })
    }

    get hasNotAvailableAssetReleasedOrStored(): boolean {
        return this.assets.some(asset => {
            return asset.assetUnavailable
                && asset.isReleasedOrStored
        })
    }

    get hasShortage(): boolean {
        return this.assets.some(a => a.isShortage)
    }

    isInvalid(params: IGetAssetsWithInvalidPeriodParams): boolean {
        return this.hasUnplannedAsset || this.getAssetsWithInvalidPeriod(params).length > 0
    }

    getAssetsWithInvalidPeriod({
        reservationStart,
        reservationEnd,
        lastNotCreditedInvoiceEndDate,
    }: IGetAssetsWithInvalidPeriodParams): IReservationCartAssetWithInvalidPeriod[] {
        return this.assets
            .filter(asset => {
                return !!asset.assetId
                    && !!asset.lmid
                    && (asset.altArticleDescription || asset.articleDescription)
                    && asset.hasInvalidPeriod({
                        reservationStart,
                        reservationEnd,
                        lastNotCreditedInvoiceEndDate,
                        assetStart: asset.start || null,
                        assetEnd: asset.end || null,
                    })

            })
            .map(asset => ({
                assetId: asset.assetId!,
                lmid: asset.lmid!,
                articleDescription: asset.articleDescription!,
                altArticleDescription: asset.altArticleDescription,
                isLabeled: asset.isLabeled,
            }))
    }

    get hasOnlyUnplannedAssets(): boolean {
        return !this.assets.some(asset => !asset.unplanned)
    }

    static getUpdateReservationCartArticleQuantityDto(data: IUpdateReservationCartArticleQuantity): IUpdateReservationCartArticleQuantityDto {
        return {
            articleId: data.articleId,
            quantity: data.quantity,
            start: data.start ? toDateTimeWithOffset(data.start) : data.start,
            end: data.end ? toDateTimeWithOffset(data.end) : data.end,
        }
    }
}
